import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import ApiService from '../services/ApiService';
import { getGA4ClientId, getGA4SessionId } from '../utils/ga4';

const OrderContext = createContext();

const STORAGE_KEY = 'gh3d_quote_session';

export const useOrder = () => {
    const context = useContext(OrderContext);
    if (!context) {
        throw new Error('useOrder must be used within OrderProvider');
    }
    return context;
};

export const OrderProvider = ({ children }) => {
    const [sessionId, setSessionId] = useState(null);
    const [sessionLoading, setSessionLoading] = useState(true);
    const [cachedGaClientId, setCachedGaClientId] = useState(null);
    const [cachedGaSessionId, setCachedGaSessionId] = useState(null);

    // useRef do śledzenia pending transfer requests (deduplication)
    const transferInProgressRef = useRef(false);

    // useRef do wykrywania przejścia non-empty → empty (auto-expire sesji)
    const prevModelsLengthRef = useRef(0);

    // Funkcja do wygenerowania browser fingerprint (dla gości)
    const generateBrowserSession = () => {
        const stored = localStorage.getItem('browser_session_id');
        if (stored) return stored;

        const newId = `browser_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('browser_session_id', newId);
        return newId;
    };

    // Funkcja do odczytu z localStorage
    const loadFromStorage = () => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    models: parsed.models || [],
                    leadTimeDays: parsed.leadTimeDays || null,
                    hasActiveCalculations: false
                };
            }
        } catch (error) {
            // Cichy fail
        }
        return {
            models: [],
            leadTimeDays: null,
            hasActiveCalculations: false
        };
    };

    const [orderState, setOrderState] = useState(loadFromStorage);
    const [showGrossPrices, setShowGrossPrices] = useState(true);

    // Auto-sync do bazy danych po każdej zmianie modeli
    const syncToDatabase = async (currentSessionId, currentModels, currentLeadTime) => {
        if (!currentSessionId) {
            return;
        }

        try {
            const modelData = {
                models: currentModels,
                leadTimeDays: currentLeadTime,
                timestamp: Date.now()
            };

            await ApiService.saveSessionData(currentSessionId, modelData);
        } catch (error) {
            // Cichy fail
            // Błąd synchronizacji - cichy fail
        }
    };

    // Inicjalizacja sesji przy starcie
    useEffect(() => {
        const initializeSession = async () => {
            try {
                setSessionLoading(true);

                // Pobierz GA Client ID wcześnie (daj GA czas na inicjalizację)
                getGA4ClientId().then(id => {
                    if (id) {
                        setCachedGaClientId(id);
                    }
                }).catch(() => {
                    // Cichy fail - cache pozostaje null
                });

                // Pobierz GA Session ID wcześnie
                getGA4SessionId().then(id => {
                    if (id) {
                        setCachedGaSessionId(id);
                    }
                }).catch(() => {
                    // Cichy fail - cache pozostaje null
                });

                // Sprawdź czy jest aktywna sesja
                const browserSessionId = generateBrowserSession();
                const activeSession = await ApiService.checkActiveSession(browserSessionId);

                if (activeSession.hasActiveSession) {
                    setSessionId(activeSession.sessionId);

                    // Załaduj dane sesji z bazy danych
                    try {
                        const sessionData = await ApiService.loadSessionData(activeSession.sessionId);
                        if (sessionData.modelData && sessionData.modelData.models) {

                            // Aktualizuj localStorage i stan
                            const restoredState = {
                                models: sessionData.modelData.models,
              leadTimeDays: sessionData.modelData.leadTimeDays || null,
              hasActiveCalculations: false
                            };

                            setOrderState(restoredState);

                            // Zapisz do localStorage
                            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                                models: restoredState.models,
                                leadTimeDays: restoredState.leadTimeDays,
                                timestamp: Date.now()
                            }));
                        }
                    } catch (error) {
            // Cichy fail
                        // Kontynuuj z danymi z localStorage
                    }
                } else {

                    // WALIDACJA: Jeśli localStorage ma modele, ale baza nie ma sesji = sesja została wyczyszczona
                    const localStorageData = loadFromStorage();
                    if (localStorageData.models.length > 0) {
                        localStorage.removeItem(STORAGE_KEY);

                        // Wymuś czysty stan
                        setOrderState({
                            models: [],
                            leadTimeDays: null,
                            hasActiveCalculations: false
                        });
                    } else {
                    }

                    setSessionId(null);
                }

            } catch (error) {
            // Cichy fail
                setSessionId(null);
            } finally {
                setSessionLoading(false);
            }
        };

        initializeSession();
    }, []);


    // Nasłuchiwanie zalogowania użytkownika (dla przepięcia sesji z gościa)
    // Używamy useRef do stabilizacji - zapobiega re-instalacji listenera przy zmianach state
    const sessionIdRef = useRef(sessionId);
    const modelsRef = useRef(orderState.models);
    const leadTimeRef = useRef(orderState.leadTimeDays);

    // Aktualizuj refs przy każdej zmianie
    useEffect(() => {
        sessionIdRef.current = sessionId;
        modelsRef.current = orderState.models;
        leadTimeRef.current = orderState.leadTimeDays;
    }, [sessionId, orderState.models, orderState.leadTimeDays]);

    useEffect(() => {
        const handleUserLoggedIn = async (event) => {
            // DEDUPLICATION: Sprawdź czy transfer już jest w trakcie
            if (transferInProgressRef.current) {
                console.log('TRANSFER-SESSION -- transfer już w trakcie, pomijam duplikat');
                return;
            }

            // Sprawdź czy mamy sesję gościa do przepięcia (używamy ref dla aktualnej wartości)
            if (!sessionIdRef.current) {
                return;
            }

            // Sprawdź czy mamy modele (przepinamy tylko sesje z modelami)
            if (modelsRef.current.length === 0) {
                console.log('TRANSFER-SESSION -- brak modeli, pomijam transfer (ochrona przed nadpisaniem sesji)');
                return;
            }

            const browserSessionId = localStorage.getItem('browser_session_id');
            if (!browserSessionId) {
                return;
            }

            // Ustaw flagę - transfer w trakcie
            transferInProgressRef.current = true;

            try {
                console.log('TRANSFER-SESSION -- rozpoczynam transfer sesji', sessionIdRef.current);
                const result = await ApiService.transferGuestSession(sessionIdRef.current, browserSessionId);

                // Po przepięciu zapisz aktualne dane do bazy (dla pewności)
                await syncToDatabase(sessionIdRef.current, modelsRef.current, leadTimeRef.current);

                console.log('TRANSFER-SESSION -- transfer zakończony pomyślnie');
            } catch (error) {
                console.error('TRANSFER-SESSION -- błąd podczas transferu:', error);
            } finally {
                // Zwolnij flagę po 2 sekundach (zabezpieczenie przed szybkimi duplikatami)
                setTimeout(() => {
                    transferInProgressRef.current = false;
                }, 2000);
            }
        };

        // Nasłuchuj event userLoggedIn - listener instalowany TYLKO RAZ (pusta dependency array)
        window.addEventListener('userLoggedIn', handleUserLoggedIn);

        // Cleanup
        return () => {
            window.removeEventListener('userLoggedIn', handleUserLoggedIn);
        };
    }, []); // Pusta dependency array - listener stabilny, używa refs dla aktualnych wartości

// Nasłuchiwanie wylogowania użytkownika (dla wyczyszczenia sesji)
useEffect(() => {
    const handleUserLoggedOut = async () => {

        // Wyczyść sesję lokalnie (bez zmiany statusu w bazie)
        setOrderState({
            models: [],
            leadTimeDays: null,
            hasActiveCalculations: false
        });
        localStorage.removeItem(STORAGE_KEY);
        setSessionId(null);

    };

    // Nasłuchuj event userLoggedOut
    window.addEventListener('userLoggedOut', handleUserLoggedOut);

    // Cleanup
    return () => {
        window.removeEventListener('userLoggedOut', handleUserLoggedOut);
    };
}, []);


    // Funkcja do tworzenia nowej sesji (wywoływana z Dropzone)
    const createNewSession = async () => {
        try {
            const browserSessionId = generateBrowserSession();

            // Pobierz GA4 Client ID (użyj cache lub fallback)
            let gaClientId = cachedGaClientId;

            // Fallback: jeśli cache jest null, spróbuj ponownie
            if (!gaClientId) {
                gaClientId = await getGA4ClientId();
                // Zaktualizuj cache jeśli fallback zadziałał
                if (gaClientId) {
                    setCachedGaClientId(gaClientId);
                }
            }

            // Pobierz GA4 Session ID (użyj cache lub fallback)
            let gaSessionId = cachedGaSessionId;

            if (!gaSessionId) {
                gaSessionId = await getGA4SessionId();
                if (gaSessionId) {
                    setCachedGaSessionId(gaSessionId);
                }
            }

            const response = await ApiService.createQuoteSession(browserSessionId, gaClientId, gaSessionId);

            setSessionId(response.sessionId);
            return response.sessionId;

        } catch (error) {
            // Cichy fail
            throw error;
        }
    };

    // Funkcja do przepięcia sesji gościa na usera (wywołana po zalogowaniu)
    const transferGuestSession = async () => {
        if (!sessionId) return;

        const browserSessionId = localStorage.getItem('browser_session_id');
        if (!browserSessionId) return;

        try {
            await ApiService.transferGuestSession(sessionId, browserSessionId);

            // NOWE: Po przepięciu zapisz aktualne dane do bazy
            await syncToDatabase(sessionId, orderState.models, orderState.leadTimeDays);
        } catch (error) {
            // Cichy fail
        }
    };

    // Funkcja do ładowania konkretnej sesji (dla zapisanych wycen)
    const loadSession = async (targetSessionId) => {
        try {
            const sessionData = await ApiService.loadSessionData(targetSessionId);

            if (sessionData.modelData && sessionData.modelData.models) {

                const restoredState = {
                    models: sessionData.modelData.models,
                    leadTimeDays: sessionData.modelData.leadTimeDays || null,
                    hasActiveCalculations: false
                };

                setOrderState(restoredState);
                setSessionId(targetSessionId);

                // Zapisz do localStorage
                localStorage.setItem(STORAGE_KEY, JSON.stringify({
                    models: restoredState.models,
                    leadTimeDays: restoredState.leadTimeDays,
                    timestamp: Date.now()
                }));

                return true;
            }
        } catch (error) {
            // Cichy fail
            return false;
        }
    };

    // Zapis do localStorage przy każdej zmianie modeli + auto-sync do bazy + auto-expire pustej sesji
    useEffect(() => {
        const prevLength = prevModelsLengthRef.current;
        const currentLength = orderState.models.length;

        try {
            if (currentLength > 0) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({
                    models: orderState.models,
                    leadTimeDays: orderState.leadTimeDays,
                    timestamp: Date.now()
                }));
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }

            // Auto-sync do bazy danych
            if (sessionId) {
                syncToDatabase(sessionId, orderState.models, orderState.leadTimeDays);
            }

            // Auto-expire: gdy użytkownik usunie wszystkie modele z sesji,
            // wygaś ją (tak samo jak przycisk "← nowa wycena")
            if (prevLength > 0 && currentLength === 0 && sessionId) {
                ApiService.updateSessionStatus(sessionId, 'expired').catch(() => {
                    // Cichy fail – stan lokalny i tak zostanie wyczyszczony
                });
                setSessionId(null);
            }

        } catch (error) {
            // Cichy fail
        }

        prevModelsLengthRef.current = currentLength;
    }, [orderState.models, orderState.leadTimeDays, sessionId]);

    // Funkcja do aktualizacji modeli
    const updateModels = (models) => {
        setOrderState(prevState => ({
            ...prevState,
            models
        }));
    };

    // Funkcja do aktualizacji lead time
    const setLeadTime = (days) => {
        setOrderState(prevState => ({
            ...prevState,
            leadTimeDays: days
        }));
    };

    // Funkcja do aktualizacji stanu kalkulacji
    const setCalculationStatus = (isCalculating) => {
        setOrderState(prevState => ({
            ...prevState,
            hasActiveCalculations: isCalculating
        }));
    };

    // Funkcja do aktualizacji pojedynczego modelu
    const updateModel = (modelId, updates) => {
        setOrderState(prevState => ({
            ...prevState,
            models: prevState.models.map(model =>
            model.id === modelId
            ? { ...model, ...updates }
            : model
            )
        }));
    };

    // Funkcja do czyszczenia sesji (po złożeniu zamówienia)
    const clearSession = async (newStatus = null) => {
        // Jeśli podano nowy status i mamy sessionId, zaktualizuj status w bazie
        if (newStatus && sessionId) {
            try {
                await ApiService.updateSessionStatus(sessionId, newStatus);
            } catch (error) {
            // Cichy fail
            }
        }

        // ZAWSZE wyczyść lokalny stan (niezależnie od statusu w bazie)
        setOrderState({
            models: [],
            leadTimeDays: null,
            hasActiveCalculations: false
        });
        localStorage.removeItem(STORAGE_KEY);
        setSessionId(null);

    };

    const value = {
        ...orderState,
        sessionId,
        sessionLoading,
        cachedGaClientId,
        cachedGaSessionId,
        showGrossPrices,
        setShowGrossPrices,
        updateModels,
        updateModel,
        setCalculationStatus,
        setLeadTime,
        clearSession,
        createNewSession,
        transferGuestSession,
        loadSession
    };

    return (
        <OrderContext.Provider value={value}>
        {children}
        </OrderContext.Provider>
    );
};

export { OrderContext };
